/**
 * Sprint 18 B4: Portal History — past bookings and results
 */
import { Calendar, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { useApi } from "@/hooks/use-api";

interface JourneyStep {
  type: "booking" | "enrollment";
  id: string;
  serviceName: string;
  date: string;
  status: "completed" | "cancelled" | "no_show" | "upcoming" | "in_progress";
}

interface JourneyResponse {
  steps: JourneyStep[];
}

type HistoryStatus = "completed" | "cancelled" | "no_show";

const STATUS_ICONS = { completed: CheckCircle, cancelled: XCircle, no_show: XCircle };
const STATUS_COLORS = {
  completed: "text-[var(--color-success)]",
  cancelled: "text-[var(--color-error)]",
  no_show: "text-[var(--color-warning)]",
};

export default function PortalHistory() {
  const { data, isLoading, error } = useApi<JourneyResponse>("/v1/portal/journey");
  const items = (data?.steps ?? []).filter((step): step is JourneyStep & { status: HistoryStatus } =>
    step.status === "completed" || step.status === "cancelled" || step.status === "no_show",
  );

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Visit History</h1>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-xl bg-[var(--color-surface)] animate-pulse" />)}
        </div>
      ) : error ? (
        <div className="rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-6 text-sm text-[var(--color-error)]">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span>Failed to load visit history. {error}</span>
          </div>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-background)] p-12 text-center">
          <Calendar className="h-10 w-10 mx-auto text-[var(--color-muted-foreground)] mb-3" />
          <p className="text-[var(--color-foreground)] font-medium">No history yet</p>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-1">Your past appointments will appear here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const Icon = STATUS_ICONS[item.status];
            return (
              <div key={item.id} className="flex items-center gap-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-5 py-4">
                <Icon className={`h-5 w-5 ${STATUS_COLORS[item.status]}`} />
                <div className="flex-1">
                  <div className="font-medium text-[var(--color-foreground)]">{item.serviceName}</div>
                  <div className="text-xs text-[var(--color-muted-foreground)] capitalize">{item.type}</div>
                </div>
                <div className="text-sm text-[var(--color-muted-foreground)]">
                  {new Date(item.date).toLocaleDateString()}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
